import { TextBadge } from '@carvana/ds-badge';
import Button from '@carvana/ds-button';
import { CheckFilledCvna } from '@carvana/ds-icons';
import { ArrowRight } from 'lucide-react';
import { icon } from '@carvana/ds-styles';
import cx from 'classnames';
import React from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { Summary } from '|/app/services';
import styles from './exchange-modal.module.css';

interface ExchangeDSModalProps {
  isOpen: boolean;
  orderSummary?: Summary | null;
  onClose: () => void;
}

const ExchangeDSModal: React.FC<ExchangeDSModalProps> = ({
  isOpen,
  orderSummary,
  onClose
}) => {

  const swap = orderSummary?.swap;
  const currentVehicle = orderSummary?.vehicle;
  const swapVehicle = swap?.vehicle;

  const formatVehicleName = (
    vehicle: { year?: number; make?: string | null; model?: string | null } | null | undefined
  ) => {
    if (!vehicle) return '';
    return `${vehicle.year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <EventedModal
      isOpen={isOpen}
      analyticsId={ModalNames.ExchangeModal}
      mobileStyle="full"
      disableOutsideClose
      onClose={onClose}
      desktopWidth="640px"
      modalHeader="Vehicle exchange"
    >
      <div className={cx(styles.content, styles.modalContentPadding)}>
        <h2 className={styles.title}>You&apos;re exchanging your recent purchase</h2>

        <div className={styles.vehiclesContainer}>
          <div className={styles.vehicleCard}>
            <TextBadge priority="muted" variant="subtle">
              Recent purchase
            </TextBadge>
            <div className={styles.vehicleImage}>
              <img
                src={swapVehicle?.primaryImageUrl ?? swapVehicle?.fallbackImageUrl ?? ''}
                alt={formatVehicleName(swapVehicle)}
              />
            </div>
            <div className={styles.vehicleInfo}>
              <div className={styles.vehicleName}>{formatVehicleName(swapVehicle)}</div>
              <div className={styles.vehicleTrim}>{swapVehicle?.trim}</div>
            </div>
          </div>

          <div className={styles.arrowIcon}>
            <ArrowRight size={24} />
          </div>

          <div className={styles.vehicleCard}>
            <TextBadge priority="success" variant="subtle">
              Exchanging for
            </TextBadge>
            <div className={styles.vehicleImage}>
              <img
                src={currentVehicle?.primaryImageUrl ?? currentVehicle?.fallbackImageUrl ?? ''}
                alt={formatVehicleName(currentVehicle)}
              />
            </div>
            <div className={styles.vehicleInfo}>
              <div className={styles.vehicleName}>{formatVehicleName(currentVehicle)}</div>
              <div className={styles.vehicleTrim}>{currentVehicle?.trim}</div>
            </div>
          </div>
        </div>

        <p className={styles.description}>
          Your exchange will be complete after you schedule pickup or delivery for your new vehicle.
          We&apos;ll collect your recently purchased vehicle during that appointment.
        </p>

        <div className={styles.includedSection}>
          <p className={styles.includedTitle}>Included in your exchange:</p>
          <div className={styles.includedItems}>
            <div className={styles.includedItem}>
              <div className={styles.itemLeft}>
                <CheckFilledCvna color={icon.success} size={16} />
                <div className={styles.itemTitle}>Credit from recent purchase</div>
              </div>
              <div className={styles.itemValue}>
                {formatCurrency(orderSummary?.payment?.creditFromPreviousOrder ?? 0)}
              </div>
            </div>

            {orderSummary?.tradeIn && orderSummary.tradeIn.status === 'Applied' && (
              <div className={styles.includedItem}>
                <div className={styles.itemLeft}>
                  <CheckFilledCvna color={icon.success} size={16} />
                  <div className={styles.itemTitle}>Previous trade-in</div>
                </div>
                <div className={styles.itemValue}>
                  {orderSummary.tradeIn.year} {orderSummary.tradeIn.make}{' '}
                  {orderSummary.tradeIn.model}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.stickyButtonContainer}>
          <Button
            onClick={onClose}
            className={styles.button}
            data-analytics-id="pp_exchange_modal_okay_button"
          >
            Okay
          </Button>
        </div>
      </div>
    </EventedModal>
  );
};

export default ExchangeDSModal;
